import { useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { EmploymentType, NurseFullRole } from "../../utils/models";
import { inverseRolesMap } from "../../utils/utilFunctions";
import { useAddStaffCandidates } from "./useAddStaffCandidates";
import { useAddStaffCostPredictions } from "./useAddStaffCostPredictions";
import { useAddStaffRows } from "./useAddStaffRows";
import type { AddStaffScheduleMode, AddStaffShift } from "./useAddStaffShift";

type Args = {
	shift: AddStaffShift;
	weekIndex: number;
	dayNo: number;
	rolesToDisplay: NurseFullRole[];
	requestJobId: string | undefined;
	contractTypes: EmploymentType[];
	/** An `isCharged` caller: only staff who can run the shift as charge. */
	chargeOnly: boolean;
	showActual: boolean;
	nurseIdsToIgnore: number[] | undefined;
};

/**
 * Roles the list may show. Role mode: the one role picked in step 1. Job mode: the caller's roles — and when a
 * job-managed caller scopes by job alone (a job section's gap passes no roles), every department role, since
 * the job filter is then the whole scope; an empty list would otherwise hide everyone.
 */
const useAllowedRoles = (mode: AddStaffScheduleMode, selectedRole: NurseFullRole | undefined, rolesToDisplay: NurseFullRole[]) => {
	const { departmentNurseTypes } = useHospitalData();
	return useMemo(() => {
		if (mode === "role") return selectedRole ? [selectedRole] : rolesToDisplay;
		return rolesToDisplay.length ? rolesToDisplay : departmentNurseTypes;
	}, [mode, selectedRole, rolesToDisplay, departmentNurseTypes]);
};

/** What the list is scoped to, named for the "No one here has the RN job yet" empty state. */
export type AddStaffListScope = { kind: "job" | "role"; name: string };

/** The job the shift is booked under (by display name — it may no longer be enabled), else role mode's one role. */
const useListScope = (shift: AddStaffShift, targetJobId: string | undefined): AddStaffListScope | null => {
	const { chosenDepartment } = useHospitalData();
	return useMemo(() => {
		if (targetJobId) {
			const job = chosenDepartment.jobs?.find((candidate) => candidate.id === targetJobId);
			return job ? { kind: "job", name: job.displayName } : null;
		}
		if (shift.mode === "role" && shift.selectedRole) return { kind: "role", name: inverseRolesMap[shift.selectedRole] };
		return null;
	}, [chosenDepartment.jobs, targetJobId, shift.mode, shift.selectedRole]);
};

/** Step 2's data: who is offered for the picked shift, their predicted cost, and each one's grid row. */
export const useAddStaffList = ({ shift, weekIndex, dayNo, rolesToDisplay, requestJobId, contractTypes, chargeOnly, showActual, nurseIdsToIgnore }: Args) => {
	// The job the shift is booked under: the step-1 job, or (role mode) whatever the request asks for.
	const targetJobId = shift.mode === "job" ? shift.selectedJobId : requestJobId;
	const allowedRoles = useAllowedRoles(shift.mode, shift.selectedRole, rolesToDisplay);
	const { candidates, nurseOccurrences, hasAnyHolder } = useAddStaffCandidates({
		weekIndex,
		dayNo,
		shiftNo: shift.shiftNo,
		allowedRoles,
		jobId: targetJobId,
		contractTypes,
		chargeOnly,
		showActual,
		nurseIdsToIgnore,
	});
	const { predictions, isLoading, isAwaitingWindow } = useAddStaffCostPredictions(shift.window, targetJobId);
	const { rows, dateRange, availableCount } = useAddStaffRows({
		candidates,
		window: shift.window,
		weekIndex,
		dayNo,
		shiftNo: shift.shiftNo,
		staffingDate: shift.staffingDate,
		predictions,
		nurseOccurrences,
		showActual,
	});
	const scope = useListScope(shift, targetJobId);
	return {
		targetJobId,
		rows,
		dateRange,
		availableCount,
		isLoadingPredictions: isLoading,
		/** The rows' cost / overtime aren't this window's yet — the add must wait for them. */
		arePredictionsPending: isAwaitingWindow,
		scope,
		hasAnyHolder,
	};
};
